import { Injectable, Input } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { CVApiService } from "src/app/reusables/services/cv-api.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { cloneDeep, unionBy } from "lodash";
import { GameType } from "../models/games.model";
import { FilterMetricsParams } from "../../../reusables/metric-selector/metric-selector.model";
import { GameObject, RankingByLogin } from "src/app/dashboard/modules/cxone-games/game.models";
import { MarketplaceService } from '../services/marketplace.service';
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { MetricValues } from "../models/games-parameter.model";

interface IParticipant {
  ImageID: number;
  ImageSrc: string;
  metricsValues: MetricValues[];
  participant: {
    AlreadyJoined: boolean;
    CompanyID: number;
    DateJoined: Date;
    LoginID: number;
    LoginName: string;
    PeriodStartDate: Date;
    RelatedGroupID: number;
    RelatedGroupLevel: number;
    RelatedGroupType: number;
    RuleID: number;
    Team: string;
    TeamID: number;
  };
};

type HistoricalData = {
  [RuleID : number] : {
    [interval : `${string}-${string}`]:  {
      participant: IParticipant[];
      actionHistory: any[];
    }
  }
}

export type NavigationContext = 'landing' | 'history' | 'edit';

export type NavigationTarget =
    | { type: 'edit', game: any, folder: any }
    | { type: 'history', game: any, folder: any }
    | { type: 'back' };

@Injectable({
    providedIn: "root",
    
})
export class GamesService {
    //Rule template variables
    RuleID: number;
    CompanyID: number;
    RuleName: string;
    RuleDescription: string;
    RuleLevel: number;
    StartDate = null;
    EndDate = null;
    IntervalType = null;
    IntervalAmount: number;
    SubmittedBy: number;
    DateAdded = null;
    RuleFolder: string;
    Timestamp: string;
    IsCopy: number;
    Active: number;
    DateRemoved = null;
    RuleType: number;
    FolderID: number;
    loadGlipseOverview: boolean;
    loadGlipseGamesLanding: boolean;
    loadTimeout: any;
    private cache: Map<string, any> = new Map();
    private pendingRequests: Map<string, Promise<any>> = new Map();

    // historical data
    private historicalData: HistoricalData = {};

    private _isBackAction = false;
    private _isCancelAction = false;
    private _hasUnsavedChanges = false;

    //Specific games fields
    Category: number;
    GameFolders$: Subject<any[]> = new Subject();

    private _contextStack: NavigationContext[] = [];
    private _navigationSubject = new BehaviorSubject<NavigationTarget | null>(null);

    navigation$ = this._navigationSubject.asObservable();
    loggedInID: number;
    loggedInName: string;

    public lastSelectedInterval: any = null;
    public lastSelectedGameRuleID: number = null;
    public lastSelectedHistoryView: any = null;
    public preserveIntervalSelection: boolean = true;

    @Input() game: GameObject;
    @Input() gameStatus: string;
    @Input() ranking: RankingByLogin;

    data: any = {
        GameFolders: [], //List of the games for the landing (should be in the format of [{Key: "folder name", Value: [{Game1},{Game2}]}]
        games: [], //List of games not separated by folder
        activeGames: [], //List of games filtered down to active only
        GroupTypes: [], //Group types for the company. Used in the eligibility section of the games add/edit flow
        thisMonthParticipants: null, //List of all participants for all games this month
        metricSelectorHTML: "", //HTML of the metric selector
        localizationMessages: new Map(), //Localization messages array
        localizationGamesMessages: new Map(), //Localization for games widget
        localizationHistoryMessages: new Map(), //Localization for games history
        localizationMilestoneGameMessages: new Map(), //Localization for milestone games
        localizedValidationMessages: new Map(), //Localization messages relating to validation
        configureComponents: [], //Components that make up the add/edit flow. Example: Multiplier Madness has Choose Metrics, Setup Rewards, Add Qualifier, etc
        isLoading: false,
        currentView: 0,
        gamesList: [],
        GameFoldersForHistory: [],
        selectedGame: [],
        listOfIntervals: [],
        selectedInterval: [],
        activeFolderGames: [],
        inactiveGames: [],
        canRevert: false,
        modalResponse: "",
        gamesFolderStates: [],
        _participantsPromise: null,
        loadGlipseOverview: false,
        loadGlipseGamesLanding: false,
        loadTimeout: false,

        winCasesConnect3: [7, 56, 448, 73, 146, 292, 84, 273],
        winCasesBlackout: [511],
        totalBadges: 0,   //Value displayed in games stats tab
        gameEarningsDateTime: null,

        //Modified wrapper. This holds objects that are being created or edited in memory
        modified: {
            _old: {}, //Old value in case cancel is clicked
            item: {
                underlyingRule: {}, //The rule that makes up the base of the game
                definition: {}, //The specific game definition (defined below)
                Eligibility: {
                    AssignedGroups: [],
                },
            },

            //This is used to store things prior to creating a template. When clicking New Game, this is used until a game type is chosen and confirmed
            preConfigure: {
                Category: 0,
                GameDef: {},
                FolderID: 0,
            },

            _cleanItem(): void {
                this.item = {
                    underlyingRule: {},
                    definition: {},
                    FolderID: 0,
                    Eligibility: {
                        AssignedGroups: [],
                    },
                };
            },

            _cleanPreconfigure(): void {
                this.preConfigure = {
                    Category: 0,
                    GameDef: 0,
                };
            },

            //Start a new flow
            New(folderID: number): void {
                this._old = {};
                this._cleanItem();
                this._cleanPreconfigure();
                this.preConfigure.FolderID = folderID;
            },

            Edit(game: any, folder: any): void {
                this._old = cloneDeep(game);
                this.item = cloneDeep(game);
                this.preConfigure.FolderID = folder.FolderData.FolderID;
                this.EditConfiguration(game);
            },

            Copy:(game: any, folder: any): void => {
                this.data.modified._old = {};
                this.data.modified.item = cloneDeep(game);
                this.data.modified.item.underlyingRule.RuleName = this.getLocalizedMessage("Copy_of", "Copy of" ) + " " + game.underlyingRule.RuleName;

                //Making sure new Active Rule will be added
                this.data.modified.item.underlyingRule.RuleID = 0;
                this.data.modified.preConfigure.FolderID = folder.FolderData.FolderID;
                this.data.modified.item.underlyingRule.Active = true;
                this.data.modified.item.isActive = true;
                this.data.modified.item.underlyingRule.IsCopy = true;
                this.data.modified.EditConfiguration(game);
            },

            SelectCategory: (category: number) => {
                this.data.modified.preConfigure.Category = category;
                this.data.modified.preConfigure.CategoryType =
                    this.data.gameCategories.textFromValue(category);
            },

            SelectGameDef: (gameDef: any) => {
                this.data.modified.preConfigure.GameDef = gameDef;
            },

            //Creates a new template for a game based on the currently selected preConfigure options (single player, connect 3, etc)
            NewConfiguration: () => {
                this.data.modified._cleanItem(); //make the modified.item object reset
                this.data.modified.item.underlyingRule = this.makeNewGame(
                    this.data.modified.preConfigure.GameDef.GameType
                );
                this.data.modified.item.underlyingRule.Category =
                    this.data.modified.preConfigure.Category;
                this.data.modified.item.underlyingRule.FolderID =
                    this.data.modified.preConfigure.FolderID;
                this.data.modified.item.underlyingRule.GameType =
                    this.data.modified.preConfigure.GameDef.GameType;
                this.data.modified.item.definition = cloneDeep(this.data.modified.preConfigure.GameDef);
                this.data._updateConfigureComponents();
                this.data.lockAllComponents();
                this.data._setAllComponentCompletionStatus(false);
                this.data.unlockComponent(0);
            },

            EditConfiguration: (game: any) => {
                let def = null;
                let defMatches = this.getGameDefinitions().filter((d: any) => {
                    return d.GameType == game.underlyingRule.GameType;
                });

                if (defMatches != null && defMatches.length > 0) {
                    def = defMatches[0];
                }

                this.data.modified.item.definition = cloneDeep(def);
                this.data.modified.preConfigure.GameDef = cloneDeep(def);
                this.data._updateConfigureComponents();
            },
        },

        //Set the configureComponents based on the preconfigured options
        _updateConfigureComponents(): void {
            this.configureComponents = [];

            switch (this.modified.preConfigure.GameDef.GameType) {
                case this.gameTypes.MultiplierMadness:
                    this._setConfigureComponentsForMultiplierMadness();
                    break;

                case this.gameTypes.TopDog:
                    this._setConfigureComponentsForTopDog();
                    break;

                case this.gameTypes.Bullseye:
                    this._setConfigureComponentsForBullseye();
                    break;

                case this.gameTypes.Milestone:
                    this._setConfigureComponentsForMilestone();
                    break;

                case this.gameTypes.Percentile:
                    this._setConfigureComponentsForPercentile();
                    break;

                case this.gameTypes.Connect3:
                    this._setConfigureComponentsForConnect3();
                    break;

                case this.gameTypes.Blackout:
                    this._setConfigureComponentsForBlackout();
                    break;

                case this.gameTypes.TopTeam:
                    this._setConfigureComponentsForTopTeam();
                    break;

                case this.gameTypes.Raffle:
                    this._setConfigureComponentsForRaffle();
                    break;

                case this.gameTypes.BracketTournament:
                    this._setConfigureComponentsForBracketTournament();
                    break;
            }
        },

        lockAllComponents(): void {
            this.configureComponents.forEach((c: any) => { c.locked = true; });
        },

        unlockComponent(index: number) {
            if (index < 0 || index >= this.configureComponents.length)
                return;
            this.configureComponents[index].locked = false;
        },

        _setAllComponentCompletionStatus(status: boolean): void {
            this.configureComponents.forEach((c: any) => { c.completed = status; });
        },

        setComponentCompletionStatus(index: number, status: boolean) {
            if (index < 0 || index > this.configureComponents.length) {
                return;
            }
            this.configureComponents[index].completed = status;
        },

        //Wrappers for each type of configure components
        _setConfigureComponentsForMultiplierMadness(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_multiplierMadness,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForTopDog(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_topDog,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForBullseye(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_bullseye,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForPercentile(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_percentile,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForMilestone(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.milestoneBoard,
                defs.rewards_milestone,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForConnect3(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.connect3Board,
                defs.rewards_connect3,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForBlackout(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.connect3Board,
                defs.rewards_connect3,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForTopTeam(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_topDog,
                defs.qualifier,
                defs.schedule,
                defs.reward_estimator
            );
        },

        _setConfigureComponentsForRaffle(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.eligibility,
                defs.chooseMetrics,
                defs.rewards_raffle,
                defs.qualifier,
                defs.schedule,
                {
                    ...defs.reward_estimator,
                    locked: true,
                    options: {
                        ...defs.reward_estimator.options,
                        isRaffle: true,
                        preventsSave: false,
                    }
                }
            );
        },

        _setConfigureComponentsForBracketTournament(): void {
            let defs = this.getComponentDefinitions();
            this.configureComponents.push(
                defs.gameName,
                defs.folders,
                defs.brackets_eligibility,
                defs.chooseMetrics,
                defs.rewards_bracket_tournament,
                defs.qualifier,
                defs.schedule_bracket_tournament
            );
        },
        //Enums
        gameTypes: Object.freeze({
            MultiplierMadness: 1,
            TopDog: 2,
            Bullseye: 3,
            Percentile: 4,
            Milestone: 5,
            Connect3: 6,
            Blackout: 7,
            TopTeam: 8,
            Raffle: 9,
            BracketTournament: 10,

            textFromValue: (val: number) => {
                switch (val) {
                    case 1: return this.getLocalizedMessage("Multiplier_Madness", "Multiplier Madness");
                    case 2: return this.getLocalizedMessage("Top_Dog", "Top Dog");
                    case 3: return this.getLocalizedMessage("Bullseye", "Bullseye");
                    case 4: return this.getLocalizedMessage("Percentile", "Percentile");
                    case 5: return this.getLocalizedMessage("Milestone", "Milestone");
                    case 6: return this.getLocalizedMessage("Connect3", "Connect 3");
                    case 7: return this.getLocalizedMessage("Blackout", "Connect All");
                    case 8: return this.getLocalizedMessage("Top_Team", "Top Team");
                    case 9: return this.getLocalizedMessage("Raffle", "Raffle");
                    case 10: return this.getLocalizedMessage("Bracket_Tournament", "Bracket Tournament");
                }
            }
        }),

        gameCategories: Object.freeze({
            Singleplayer: 1,
            Multiplayer: 2,
            TeamVs: 3,

            textFromValue: (val: number) => {
                switch (val) {
                    case 1: return this.getLocalizedMessage('Single_Player', 'Single Player');
                    case 2: return this.getLocalizedMessage('Multiplayer', 'Multiplayer');
                    case 3: return this.getLocalizedMessage('Team_Vs_Team', 'Team Vs. Team');
                }
            }
        }),

        //Reward Options available for a game type. This is a Bitwise (or Flagged) enum. Meaning that a value of 3 is both Coins (1) and XP (2)
        rewardOptions: Object.freeze({
            Coins: 1,
            XP: 2,
            Currency: 4,
            Badge: 8,
            Custom: 10,
            //Gems:12,  NOTE: This is in case Gems are added to the rewards in the future.

            //Checks if the rewardOption parameter has toCheck set bitwise
            isSet: (rewardOption, toCheck) => {
                return (rewardOption & toCheck) == toCheck;
            },

            //internal function to pull each value out and return the localized text for it
            _textFromValue: (val: number) => {
                switch (val) {
                    case this.data.rewardOptions.Coins: return this.getLocalizedMessage("Coins", "Coins");
                    case this.data.rewardOptions.XP: return this.getLocalizedMessage("XP", "XP");
                    case this.data.rewardOptions.Currency: return this.getLocalizedMessage("Currency", "Currency");
                    case this.data.rewardOptions.Badge: return this.getLocalizedMessage("Badge", "Badge");
                   // case this.data.rewardOptions.Gems: return this.getLocalizedMessage("Gems", "Gems");  NOTE: This is in case Gems are added to the rewards in the future.
                }
            },

            //Public function to determine the text for the passed in rewardOption(s)
            textFromValue: (rewardOption) => {
                let text = [];
                if (this.data.rewardOptions.isSet(rewardOption, this.data.rewardOptionsCoins))
                    text.push(this.data.rewardOptions._textFromValue(this.data.rewardOptions.Coins));
                if (this.data.rewardOptions.isSet(rewardOption, this.data.rewardOptions.XP))
                    text.push(this.data.rewardOptions._textFromValue(this.data.rewardOptions.XP));
                if (this.data.rewardOptions.isSet(rewardOption, this.data.rewardOptions.Currency))
                    text.push(this.data.rewardOptions._textFromValue(this.data.rewardOptions.Currency));
                if (this.data.rewardOptions.isSet(rewardOption, this.data.rewardOptions.Badge))
                    text.push(this.data.rewardOptions._textFromValue(this.data.rewardOptions.Badge));
                // NOTE: This is in case Gems are added to the rewards in the future.
                // if (this.data.rewardOptions.isSet(rewardOption, this.data.rewardOptions.Gems))
                //     text.push(this.data.rewardOptions._textFromValue(this.data.rewardOptions.Gems));
                return text.join(", ");
            }
        }),

        gameIntervals: Object.freeze({
            Day: 1,
            Week: 2,
            Month: 3,

            textFromValue: (val: number) => {
                switch (val) {
                    case 1: return this.getLocalizedMessage('Daily', 'Daily');
                    case 2: return this.getLocalizedMessage('Weekly', 'Weekly');
                    case 3: return this.getLocalizedMessage('Monthly', 'Monthly');
                }
            }
        }),

        getComponentDefinitions(): any {
            return {
                gameName: {
                    name: "gameName",
                    options: {
                        unlocks: "next",
                        preventSave: true
                    }
                },
                folders: {
                    name: "folders",
                    options: {
                        unlocks: "next",
                        preventSave: true
                    }
                },
                chooseMetrics: {
                    name: "chooseMetrics",
                    options: {
                        maxMetrics: 1,
                        unlocks: "next",
                        preventsSave: true,
                    },
                },
                qualifier: {
                    name: "qualifier",
                    options: {
                        maxMetrics: 1,
                        unlocks: "next",
                        preventsSave: false
                    }
                },
                schedule: {
                    name: "schedule",
                    options: {
                        type: "standardCalendar",
                        unlocks: "next",
                        preventsSave: false
                    }
                },
                schedule_bracket_tournament: {
                    name: "bracketTournamentSchedule",
                    options: {
                        type: "standardCalendar",
                        unlocks: "next",
                        preventsSave: false
                    }
                },
                eligibility: {
                    name: "eligibility",
                    options: {
                        type: "assignGroups",
                        unlocks: "next",
                        preventsSave: false
                    }
                },
                brackets_eligibility: {
                    name: "brackets_eligibility",
                    options: {
                        type: "assignGroups",
                        unlocks: "next",
                        preventsSave: false
                    }
                },
                rewards_multiplierMadness: {
                    name: "multiplierMadnessRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                rewards_topDog: {
                    name: "topDogRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                rewards_bullseye: {
                    name: "bullseyeRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                rewards_milestone: {
                    name: "milestoneRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next",
                        preventsSave: true,
                    },
                },
                rewards_percentile: {
                    name: "percentileRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next",
                        preventsSave: true,
                    },
                },
                connect3Metrics: {
                    name: "connect3Metrics",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next",
                        preventsSave: true,
                    },
                },
                connect3Board: {
                    name: "connect3Board",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next",
                        preventsSave: true,
                        maxMetrics: 1,
                    },
                },
                rewards_connect3: {
                    name: "connect3Rewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                milestoneBoard: {
                    name: "milestoneBoard",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next",
                        preventsSave: true,
                    },
                },
                rewards_topTeam: {
                    name: "topTeamRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                rewards_raffle: {
                    name: "raffleRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                rewards_bracket_tournament: {
                    name: "bracketTournamentRewards",
                    options: {
                        rewardsAvailable:
                            this.modified.preConfigure.GameDef.RewardOptions,
                        unlocks: "next2",
                        preventsSave: true,
                    },
                },
                reward_estimator: {
                    name: "rewardEstimator",
                    options: {
                        unlocks: "next",
                        preventsSave: true
                    }
                }
            };
        },

        getOpacityMM (gamedata: any) {    
            if ((gamedata.MultiplierMadnessRewards.maxCoinsPayout == 0 || gamedata.MultiplierMadnessRewards.maxCoinsPayout == null)
                && (gamedata.MultiplierMadnessRewards.maxXpPayout == 0 || gamedata.MultiplierMadnessRewards.maxXpPayout == null)
                && (gamedata.MultiplierMadnessRewards.maxCurrencyPayout == 0 || gamedata.MultiplierMadnessRewards.maxCurrencyPayout == null))
            return "noMaxPayout";
        },
    
        getBadgeUsingIndex(game: any, index: number) {
            var reward = null;
            //if (game.MultiplierMadnessRewards != null) { reward = (game.MultiplierMadnessRewards.Badge != null) ? game.MultiplierMadnessRewards.Badge : null; }
            if (game.TopDogRewards != null) {
                if (game.TopDogRewards.length > index)
                    if (game.TopDogRewards[index].Badge != undefined || game.TopDogRewards[index].Badge != null) {
                        reward = game.TopDogRewards[index].Badge.rewardValue
                    } else {
                        reward = null;
                    }
                } // get 1st place reward
            else if (game.BullseyeRewards != null) { reward = (game.BullseyeRewards.length != 0 && game.BullseyeRewards[index].Badge != null) ? game.BullseyeRewards[index].Badge.rewardValue : null; }
            else if (game.MilestoneRewards != null) { reward = (game.MilestoneRewards.length != 0 && game.MilestoneRewards[index].Badge != null) ? game.MilestoneRewards[index].Badge.rewardValue : null; }
            else if (game.BracketTournamentRewards != null) { reward = (game.BracketTournamentRewards.length != 0 && game.BracketTournamentRewards[index].Badge != null) ? game.BracketTournamentRewards[index].Badge.rewardValue : null; }

            if (reward == null || reward == 0) return null;
            return "../Admin/ImageResizer.aspx?imgid=" + reward + "&w=24&h=24";
        }
    };

    constructor(
        private cvApiService: CVApiService,
        private cvLocalizationService: CVLocalizationService,
        private marketplaceService: MarketplaceService,
        public cvConfigService: CvConfigurationService
    ) {
        this.cvLocalizationService.GetSection("Games").then((result) => {
            this.data.localizationMessages = result;
        });

        this.cvLocalizationService.GetSection("Games_Module").then((result) => {
            this.data.localizationGamesMessages = result;
        });

        this.cvLocalizationService.GetSection("Games_History").then((result) => {
            this.data.localizationHistoryMessages = result;
        });

        this.cvLocalizationService.GetSection("Milestone_Game").then((result) => {
            this.data.localizationMilestoneGameMessages = result;
        });

        this.cvLocalizationService.GetSection("Common").then((result) => {
            this.data.localizationCommonMessages = result;
        });
        
        this.cvLocalizationService.GetSection("Games_Validation").then((result) => {
            this.data.localizedValidationMessages = result;
        });
    }

    get isBackAction(): boolean {
        return this._isBackAction;
    }

    set isBackAction(value: boolean) {
        this._isBackAction = value;
    }

    get isCancelAction(): boolean {
        return this._isCancelAction;
    }

    set isCancelAction(value: boolean) {
        this._isCancelAction = value;
    }

    get hasUnsavedChanges(): boolean {
        return this._hasUnsavedChanges;
    }

    set hasUnsavedChanges(value: boolean) {
        this._hasUnsavedChanges = value;
    }

    resetNavigationFlags(): void {
        this._isBackAction = false;
        this._isCancelAction = false;
        this._hasUnsavedChanges = false;
    }

    getLocalizedMessage(key: string, fallback: string): string {
        if (this.data.localizationMessages && this.data.localizationMessages.has(key)) {
            return this.data.localizationMessages.get(key);
        } else {
            return fallback;
        }
    }

    getGamesWidgetMessage(key: string, fallback: string): string {
        if (this.data.localizationGamesMessages && this.data.localizationGamesMessages.has(key)) {
            return this.data.localizationGamesMessages.get(key);
        } else {
            return fallback;
        }
    }

    getGamesHistoryMessages(key: string, fallback: string): string {
        if (this.data.localizationHistoryMessages && this.data.localizationHistoryMessages.has(key)) {
            return this.data.localizationHistoryMessages.get(key);
        } else {
            return fallback;
        }
    }

    getMilestoneGameMessages(key: string, fallback: string): string {
        if (this.data.localizationMilestoneGameMessages && this.data.localizationMilestoneGameMessages.has(key)) {
            return this.data.localizationMilestoneGameMessages.get(key);
        } else {
            return fallback;
        }
    }

    getValidationMessage(key: string, fallback: string): string {
        if (
            this.data.localizedValidationMessages &&
            this.data.localizedValidationMessages.has(key)
        ) {
            return this.data.localizedValidationMessages.get(key);
        } else {
            return fallback;
        }
    }

    getCommonMessage(key: string, fallback: string): string {
        if (this.data.localizationCommonMessages && this.data.localizationCommonMessages.has(key)) {
            return this.data.localizationCommonMessages.get(key);
        } else {
            return fallback;
        }
    }

    async loadGamesForRewards(startDate: any) {
        try {
            let url = "/api/rewards/load-games-rewards/" + startDate;
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(
                "Error loading games for rewards: " + error.message
            );
        }
    }

    async loadMetricTargets(metricID: number) {
        try {
            let url =
                "/api/v1/gamification/games/connect3/metricType?metricID=" +
                metricID;
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading metric targets" + error.message);
        }
    }

    async loadGamesLanding(getInactive: boolean) {
        if (this.data.isLoading) return;

        this.data.isLoading = true;

        try {
            let url = "/api/v1/gamification/games";
            if (getInactive) {
                url += "?inactiveOnly=true";
            }

            let response: any = await this.cvApiService.get(url);
            if (response.status_code == 200) {
                if (getInactive) {
                    this.data.inactiveGames = response.data.GameFolders;
                } else {
                    this.data.activeFolderGames = response.data.GameFolders;
                    this.data.canRevert = response.data.canRevert;
                }

                if (this.data.inactiveGames.length > 0) {
                    let TempFolders = [];

                    this.data.inactiveGames.forEach((value: any) => {
                        let Folder = this.data.activeFolderGames.find(
                            (element: any) => {
                                return (
                                    element.FolderData.FolderID ==
                                    value.FolderData.FolderID
                                );
                            }
                        );

                        //Added the use of unionBy to prevent duplicates when concatenating
                        Folder.Games = unionBy(
                            value.Games,
                            Folder.Games,
                            "GameObject.underlyingRule.RuleID"
                        );

                        TempFolders.push(Folder);
                    });

                    this.data.GameFolders = TempFolders;
                } else {
                    this.data.GameFolders = this.data.activeFolderGames;
                }

                this.data.GameFolders.forEach((item: any) => {
                    this.data.gamesFolderStates[item.FolderData.FolderID] =
                        true;
                });

                this.data.GroupTypes = response.data.GroupTypes;

                // Create a list of games only (not by folder)
                this.data.games = this.data.GameFolders.reduce(
                    (games: any, folder: any) => {
                        Array.prototype.push.apply(
                            games,
                            folder.Games.map((game: any) => {
                                game.Folder = folder.FolderData;
                                return game.GameObject;
                            })
                        );
                        return games;
                    },
                    []
                );

                this.GameFolders$.next(this.data.GameFolders);

                this.data.activeGames = this.data.games.filter((game: any) => {
                    let startDate = new Date(
                        game.underlyingRule.StartDate + "-06:00"
                    );
                    let endDate =
                        game.underlyingRule.EndDate != null
                            ? new Date(game.underlyingRule.EndDate + "-06:00")
                            : null;
                    let now = new Date();
                    return (
                        game.isActive &&
                        startDate <= now &&
                        (endDate == null || now <= endDate)
                    );
                });

                this.data.isLoading = false;
            } else {
                this.data.isLoading = false;
                throw response;
            }
        } catch (error) {
            this.data.isLoading = false;
            showErrorMessage("Error loading games: " + error.message);
        }
    }

    async loadParticipants() {
        try {
            let url = "/api/v1/gamification/games/participants";
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                this.data.thisMonthParticipants = response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(
                "Error loading game participants: " + error.message
            );
        }
    }

    async loadGamesListForHistory() {
        this.data.isLoading = true;

        try {
            let url = "/api/v1/gamification/game-history";
            let response: any = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                this.data.GameFoldersForHistory = response.data.GameFolders;
                this.data.isLoading = false;
            } else {
                this.data.isLoading = false;
                throw response;
            }
        } catch (error) {
            this.data.isLoading = false;
            showErrorMessage("Error loading game history: " + error.message);
        }
    }

    async joinGame(gameId: number) {
        try {
            let url = "/api/rewards/joingame/" + gameId;
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                return response;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error joining game: " + error.message);
        }
    }

    async saveGame(game: any, loadProducts: boolean = false) {
        try {
            let url = "/api/v1/gamification/games";

            let response = await this.cvApiService.post(url, game);

            if (response.status_code >= 200 && response.status_code < 300) {
                if(loadProducts){
                    await this.marketplaceService.loadProducts(true);
                }
                return response;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error saving game: " + error.message);
        }
    }

    async deleteGame(game: any) {
        try {
            let url =
                "/api/v1/gamification/games/" +
                game.GameObject.underlyingRule.RuleID;
            let response = await this.cvApiService.delete(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (error) {
            showErrorMessage("Error deleting game: " + error.message);
        }
    }

    async activateGame(game: any) {
        try {
            let url =
                "/api/v1/gamification/games/" +
                game.GameObject.underlyingRule.RuleID;
            let response = await this.cvApiService.put(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (error) {
            showErrorMessage(
                "Error activating/deactivating game: " + error.message
            );
        }
    }

    async getParticipantsByRule(ruleID, selectedStartDate, selectedEndDate, bracketID) {
      // if we already did this request, there is no need to do it again
      if (this.historicalData[ruleID] && this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`] && this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`].participant) {
        return this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`].participant;
      }
      try {
        let url =
          "/api/v1/gamification/game/" + ruleID +
          "/participants?start=" + selectedStartDate +
          "&end=" + selectedEndDate +
          "&bracketID=" + bracketID;
        let response = await this.cvApiService.get(url);

        if (response.status_code == 200) {
          if (!this.historicalData[ruleID]) {
            this.historicalData = {
              ...this.historicalData,
              [ruleID]: {},
            };
          }
          this.historicalData[ruleID] = {
            ...this.historicalData[ruleID],
            [`${selectedStartDate}-${selectedEndDate}`]: {
              ...this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`],
              participant: response.data
            },
          };
          return response.data;
        } else {
          throw response;
        }
      } catch (error) {
        showErrorMessage(
          "Error loading rule participants: " + error.message
        );
      }
    }

    async getBracketMatchesByVersion(ruleID, version) {
        try {
            let url = `/api/v1/gamification/game/${ruleID}/bracket/${version}`;
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(
                "Error loading bracket version: " + error.message
            );
        }
    }

    async getRewardsByGameInterval(ruleID, selectedStartDate, selectedEndDate) {
      if (this.historicalData[ruleID] && this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`] && this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`].actionHistory) {
        return this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`].actionHistory;
      }
      try {
        let url =
          "/api/v1/gamification/games/" +
          ruleID +
          "/rewards?start=" +
          selectedStartDate +
          "&end=" +
          selectedEndDate;
        let response = await this.cvApiService.get(url);

        if (response.status_code == 200) {
          if (!this.historicalData[ruleID]) {
            this.historicalData = {
              ...this.historicalData,
              [ruleID]: {},
            };
          }
          this.historicalData[ruleID] = {
            ...this.historicalData[ruleID],
            [`${selectedStartDate}-${selectedEndDate}`]: {
              ...this.historicalData[ruleID][`${selectedStartDate}-${selectedEndDate}`],
              actionHistory: response.data,
            },
          };
          return response.data;
        } else {
          throw response;
        }
      } catch (error) {
        showErrorMessage(
          "Error loading rewards for the selected game interval"
        );
      }
    }

    clearRewardsHistory(ruleId?: number) {
        if (!ruleId) this.historicalData = {};
        if (!this.historicalData[ruleId]) return;
        this.historicalData[ruleId] = {};
    }

    async getEstimatorData(selectedStartDate, selectedEndDate, game) {
        try {
            let url =
                "/api/v1/gamification/reward-estimator?start=" +
                selectedStartDate +
                "&end=" +
                selectedEndDate;
            let response = await this.cvApiService.post(
                url,
                JSON.stringify(game)
            );

            if (response.status_code >= 200 && response.status_code < 300) {
                return response;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error getting estimator data: " + error.message);
        }
    }

    async getExportEstimatorData(selectedStartDate, selectedEndDate, dataObj) {
        try {
            let url =
                "/api/v1/gamification/reward-estimator/export?format=excel&start=" +
                selectedStartDate +
                "&end=" +
                selectedEndDate;
            let response = await this.cvApiService.post(
                url,
                JSON.stringify(dataObj)
            );

            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(
                "Error exporting estimator dara: " + error.message
            );
        }
    }

    async revertGamePayment(payoutsData: any, logins: any) {
        try {
            let url =
                "/api/v1/gamification/game/" +
                payoutsData.ruleID +
                "/reversion?ruleName=" +
                payoutsData.ruleName +
                "&start=" +
                payoutsData.startDate +
                "&end=" +
                payoutsData.endDate;
            let response = await this.cvApiService.post(
                url,
                JSON.stringify(logins)
            );

            if (response.status_code >= 200 && response.status_code < 300) {
                return response;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error reverting game payment: " + error.message);
        }
    }

    async loadGroupTypes() {
        try {
            let url = "/api/v1/group_types";
            let response = await this.cvApiService.get(url);

            if (response.status_code == 200) return response.data;
            else throw response;
        } catch (error) {
            showErrorMessage("Error loading group types: " + error.message);
        }
    }

    getGamesFolders$(): Observable<any[]> {
        return this.GameFolders$.asObservable();
    }

    _extractRecInfo(): any {
        let recInfo = { GroupLevel: 5, GroupID: 0 };

        if (
            this.data != null &&
            this.data.modified != null &&
            this.data.modified.item != null &&
            this.data.modified.item.Eligibility != null &&
            this.data.modified.item.Eligibility.AssignedGroups != null &&
            this.data.modified.item.Eligibility.AssignedGroups.rec != null &&
            this.data.modified.item.Eligibility.AssignedGroups.rec.length > 0
        ) {
            recInfo.GroupID =
                this.data.modified.item.Eligibility.AssignedGroups.rec[0].GroupID;
            recInfo.GroupLevel =
                this.data.modified.item.Eligibility.AssignedGroups.rec[0].GroupLevel;
        }

        return recInfo;
    }

    makeNewGame(gameType: number): any {
        switch (gameType) {
            case this.data.gameTypes.MultiplierMadness:
                return this.multiplierMadnessTemplate();
            case this.data.gameTypes.TopDog:
                return this.topDogTemplate();
            case this.data.gameTypes.Bullseye:
                return this.bullseyeTemplate();
            case this.data.gameTypes.Percentile:
                return this.percentileTemplate();
            case this.data.gameTypes.Milestone:
                return this.milestoneTemplate();
            case this.data.gameTypes.Connect3:
                return this.connect3Template();
            case this.data.gameTypes.Blackout:
                return this.blackoutTemplate();
            case this.data.gameTypes.TopTeam:
                return this.topTeamTemplate();
            case this.data.gameTypes.Raffle:
                return this.raffleTemplate();
            case this.data.gameTypes.BracketTournament:
                return this.bracketTournamentTemplate();
        }
    }

    //Returns the array of game definitions. This is where a game and its options are defined. Such as the Name, Descriptions, Rewards Available, etc
    getGameDefinitions(): any {
        var rewards = this.data.rewardOptions;
        return [
            {
                //Mulitplier Madness
                GameType: this.data.gameTypes.MultiplierMadness,
                Name: this.getLocalizedMessage(
                    "Multiplier_Madness",
                    "Multiplier Madness"
                ),
                IconClass: "gtiMultiplierMadness",
                BackgroundColor: "#F38794",
                ShortDescription: this.getLocalizedMessage(
                    "Multiplier_Madness_Summary",
                    "The better players perform on a selected metric, the greater their earnings!"
                ),
                DetailDescription1: this.getLocalizedMessage(
                    "Multiplier_Madness_Desc_1",
                    "Watch player engagement increase as they see their rewards continue to grow!"
                ),
                DetailDescription2: this.getLocalizedMessage(
                    "Multiplier_Madness_Desc_2",
                    "Choose a KPI that you want to increase. For example, you could make every sale worth 5 coins. Ten sales would earn 50 coins!"
                ),
                SpecialNotes: this.getLocalizedMessage(
                    "Multiplier_Madness_Special_Notes",
                    "Should only be used for count-type metrics!"
                ),
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Multiplier_Madness_Example_Line1_1",
                    "KPI is"
                ),
                ExampleLine1Underlined: this.getLocalizedMessage(
                    "Multiplier_Madness_Example_Line1_2",
                    "units sold"
                ),
                ExampleLine2: this.getLocalizedMessage(
                    "Multiplier_Madness_Example_Line2",
                    "Multiplier = 100 coins/sale"
                ),
                ExampleLine3: this.getLocalizedMessage(
                    "Multiplier_Madness_Example_Line3",
                    "An agent gets 5 sales ---> 500 coins"
                ),
                ExampleLine4: "",
                MetricType: "Count based",
                RewardOptions: rewards.Coins | rewards.XP | rewards.Currency,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
            },
            {
                //Top Dog
                GameType: this.data.gameTypes.TopDog,
                Name: this.getLocalizedMessage("Top_Dog", "Top Dog"),
                IconClass: "gtiTopDog",
                BackgroundColor: "#EBA46F",
                ShortDescription: this.getLocalizedMessage(
                    "Top_Dog_Summary",
                    "The Top Dog is the player that performs the best on a selected metric!"
                ),
                DetailDescription1: this.getLocalizedMessage(
                    "Top_Dog_Desc_1",
                    "Setup your leaderboard and watch players compete to see who will be the Top Dog!"
                ),
                DetailDescription2: this.getLocalizedMessage(
                    "Top_Dog_Desc_2",
                    "If you choose calls as the KPI, the player with the most calls would be crowned the Top Dog. You may also choose to reward 2nd, 3rd, 4th place and so on."
                ),
                SpecialNotes: "",
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Top_Dog_Example_Line1_1",
                    "KPI is"
                ),
                ExampleLine1Underlined: this.getLocalizedMessage(
                    "Top_Dog_Example_Line1_2",
                    "QM score"
                ),
                ExampleLine2: this.getLocalizedMessage(
                    "Top_Dog_Example_Line2",
                    "1st place ---> 1000 coins"
                ),
                ExampleLine3: this.getLocalizedMessage(
                    "Top_Dog_Example_Line3",
                    "2nd place ---> 500 coins"
                ),
                ExampleLine4: this.getLocalizedMessage(
                    "Top_Dog_Example_Line4",
                    "3rd place ---> 100 coins"
                ),
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Multiplayer],
            },
            {
                //Bullseye
                GameType: this.data.gameTypes.Bullseye,
                Name: this.getLocalizedMessage("Bullseye", "Bullseye"),
                IconClass: "gtiBullseye",
                BackgroundColor: "#7BCCC8",
                ShortDescription: this.getLocalizedMessage(
                    "Bullseye_Summary",
                    "The closer players get to the bullseye, the more they will earn!"
                ),
                DetailDescription1: this.getLocalizedMessage(
                    "Bullseye_Desc_1",
                    "The closer players get to reaching a chosen KPI, the closer they will be to the bullseye! A perfect score hits the target."
                ),
                DetailDescription2: this.getLocalizedMessage(
                    "Bullseye_Desc_2",
                    "You setup the target board and players who reach the KPI hit the bullseye. Players can also be rewarded for getting close to the KPI in which case they would hit a ring outside the bullseye."
                ),
                SpecialNotes: "",
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Bullseye_Example_Line1_1",
                    "KPI is"
                ),
                ExampleLine1Underlined: this.getLocalizedMessage(
                    "Bullseye_Example_Line1_2",
                    "Agent Efficiency"
                ),
                ExampleLine2: this.getLocalizedMessage(
                    "Bullseye_Example_Line2",
                    "95% to 100% ---> 1000 coins"
                ),
                ExampleLine3: this.getLocalizedMessage(
                    "Bullseye_Example_Line3",
                    "92% to 94.99% ---> 500 coins"
                ),
                ExampleLine4: this.getLocalizedMessage(
                    "Bullseye_Example_Line4",
                    "90% to 91.99% ---> 100 coins"
                ),
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
            },
            /* Game not implemented { //Percentile
            GameType: this.data.gameTypes.Percentile,
            Name: this.getLocalizedMessage("Percentile", "Percentile"),
            IconClass: "gtiPercentile",
            BackgroundColor: "#6AC07D",
            ShortDescription: this.getLocalizedMessage("Percentile_Summary", "Players who perform in the top percentage of the group win the big prize!"),
            DetailDescription1: "Percentile detail description 1 here",
            DetailDescription2: "Percentile detail description 2 here",
            SpecialNotes: "",
            ExampleLine1Preunderline: "", //not implemented yet
            ExampleLine1Underlined: "", //
            ExampleLine2: "",           //
            ExampleLine3: "",           //
            ExampleLine4: "",           //
            MetricType: "Count based",
            RewardOptions: rewards.Coins | rewards.XP | rewards.Currency | rewards.Badge,
            RewardOptionsText: (RewardOptions) { return this.data.rewardOptions.textFromValue(RewardOptions) },
            Active: 0,
            Categories: [this.data.gameCategories.Multiplayer]
            },*/
            {
                //Milestone
                GameType: this.data.gameTypes.Milestone,
                Name: this.getLocalizedMessage("Milestone", "Milestone"),
                IconClass: "gtiMilestone",
                BackgroundColor: "#82B4E0",
                ShortDescription: this.getLocalizedMessage(
                    "Milestone_Summary",
                    "Reward players once for accomplishing something special!"
                ),
                DetailDescription1: "Milestone detail description 1 here",
                DetailDescription2: "Milestone detail description 2 here",
                SpecialNotes: "",
                ExampleLine1Preunderline: "", //not implemented yet
                ExampleLine1Underlined: "", //
                ExampleLine2: "", //
                ExampleLine3: "", //
                ExampleLine4: "", //
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
                Themes: [
                    {
                        themeID: 1,
                        themeName: "Space chart",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/space/",
                    },
                    {
                        themeID: 2,
                        themeName: "Pirate Map",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/pirate/",
                    },
                    {
                        themeID: 3,
                        themeName: "Race track",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/racing/",
                    },
                    {
                        themeID: 4,
                        themeName: "Camping peak",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/camping/",
                    },
                    {
                        themeID: 5,
                        themeName: "Snowy mountain",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/snow/",
                    },
                    {
                        themeID: 6,
                        themeName: "Mountain hike",
                        themePath:
                            "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/hiking/",
                    },
                ],
            },
            {
                //Connect 3
                GameType: this.data.gameTypes.Connect3,
                Name: this.getLocalizedMessage("Connect3", "Connect 3"),
                IconClass: "gtiConnect3",
                BackgroundColor: "#BE9FCA",
                ShortDescription: this.getLocalizedMessage(
                    "Connect3_Summary",
                    "Players earn the prize by getting three in a row"
                ),
                DetailDescription1: [
                    {
                        name: "AHT",
                        direction: 0,
                        target: "00:02:00",
                        completed: 0,
                    },
                    {
                        name: "CPH",
                        direction: 0,
                        target: "00:02:00",
                        completed: 0,
                    },
                    { name: "Calls", direction: 1, target: "45", completed: 0 },
                    { name: "FCR", direction: 1, target: "10", completed: 0 },
                    {
                        name: "Free Space",
                        direction: 0,
                        target: null,
                        completed: 1,
                    },
                    { name: "Sales", direction: 0, target: "30", completed: 1 },
                    { name: "ACR", direction: 0, target: "8", completed: 0 },
                    { name: "NPS", direction: 1, target: "5", completed: 0 },
                    {
                        name: "ASA",
                        direction: 0,
                        target: "00:00:50",
                        completed: 1,
                    },
                ], //Object to create example gameboard and for localizing purposes
                DetailDescription2: "Connect 3 detail description 2 here",
                SpecialNotes: "",
                ExampleLine1Preunderline: "", //not implemented yet
                ExampleLine1Underlined: "", //
                ExampleLine2: "", //
                ExampleLine3: "", //
                ExampleLine4: "", //
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
            },
            {
                //Blackout - Connect All
                GameType: this.data.gameTypes.Blackout,
                Name: this.getLocalizedMessage("Blackout", "Connect All"),
                IconClass: "gtiBlackout",
                BackgroundColor: "#2EA0B9",
                ShortDescription: this.getLocalizedMessage(
                    "Blackout_Summary",
                    "Players earn the prize by completing the metric goal on every square!"
                ),
                DetailDescription1: [
                    { name: "Sales", direction: 0, target: "30", completed: 1 },
                    {
                        name: "AHT",
                        direction: 0,
                        target: "00:02:00",
                        completed: 0,
                    },
                    { name: "Calls", direction: 1, target: "45", completed: 0 },
                    { name: "FCR", direction: 1, target: "10", completed: 0 },
                    {
                        name: "Free Space",
                        direction: 0,
                        target: null,
                        completed: 1,
                    },
                    {
                        name: "ASA",
                        direction: 0,
                        target: "00:00:50",
                        completed: 1,
                    },
                    { name: "ACR", direction: 0, target: "8", completed: 0 },
                    { name: "NPS", direction: 1, target: "5", completed: 0 },
                    {
                        name: "CPH",
                        direction: 0,
                        target: "00:02:00",
                        completed: 0,
                    },
                ], //Object to create example gameboard and for localizing purposes
                DetailDescription2: "Blackout detail description 2 here",
                SpecialNotes: "",
                ExampleLine1Preunderline: "", //not implemented yet
                ExampleLine1Underlined: "", //
                ExampleLine2: "", //
                ExampleLine3: "", //
                ExampleLine4: "", //
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
            },
            {
                //Top Team
                GameType: this.data.gameTypes.TopTeam,
                Name: this.getLocalizedMessage("Top_Team", "Top Team"),
                IconClass: "gtiTopTeam",
                BackgroundColor: "#81C157",
                ShortDescription: this.getLocalizedMessage(
                    "Top_Team_Summary",
                    '"Teams" compete against each other and win awards according to the position in which they place.'
                ),
                DetailDescription1: this.getLocalizedMessage(
                    "Top_Team_Desc_1",
                    "Setup your leaderboard and watch players compete to see who will be the Top Team!"
                ),
                DetailDescription2: this.getLocalizedMessage(
                    "Top_Team_Desc_2",
                    "If you choose calls as the KPI, the player with the most calls would be crowned the Top Team. You may also choose to reward 2nd, 3rd, 4th place and so on."
                ),
                SpecialNotes: "",
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Top_Dog_Example_Line1_1",
                    "KPI is"
                ),
                ExampleLine1Underlined: this.getLocalizedMessage(
                    "Top_Dog_Example_Line1_2",
                    "QM score"
                ),
                ExampleLine2: this.getLocalizedMessage(
                    "Top_Dog_Example_Line2",
                    "1st place ---> 1000 coins"
                ),
                ExampleLine3: this.getLocalizedMessage(
                    "Top_Dog_Example_Line3",
                    "2nd place ---> 500 coins"
                ),
                ExampleLine4: this.getLocalizedMessage(
                    "Top_Dog_Example_Line4",
                    "3rd place ---> 100 coins"
                ),
                MetricType: "Count based",
                RewardOptions:
                    rewards.Coins |
                    rewards.XP |
                    rewards.Currency |
                    rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.TeamVs],
            },
            {
                //Raffle
                GameType: this.data.gameTypes.Raffle,
                Name: this.getLocalizedMessage("Raffle", "Raffle"),
                IconClass: "gtiRaffle",
                BackgroundColor: "#F38794",
                ShortDescription: this.getLocalizedMessage(
                    "Raffle_Summary",
                    "Players earn a raffle ticket through performance. Tickets are randomly drawn and prizes are distributed to the winners."
                ),
                DetailDescription1: "",
                DetailDescription2: "",
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Raffle_Example_Line1_1",
                    "Metric goals achievement"
                ),
                ExampleLine2: this.getLocalizedMessage(
                    "Raffle_Example_Line2",
                    "Prize 1 = 100 coins"
                ),
                ExampleLine3: this.getLocalizedMessage(
                    "Raffle_Example_Line3",
                    "Prize 2 = Amazon gift card"
                ),
                ExampleLine4: "",
                MetricType: "Count based",
                RewardOptions: rewards.Coins | rewards.XP | rewards.Custom,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Singleplayer],
            },
            {
                //Bracket Competition
                GameType: this.data.gameTypes.BracketTournament,
                Name: this.getLocalizedMessage("Bracket_Tournament", "Bracket Tournament"),
                IconClass: "gtiBracketTournament",
                BackgroundColor: "#F38794",
                ShortDescription: this.getLocalizedMessage(
                    "Bracket_Tournament_Summary",
                    "Participants compete individual matches to get to the final and win a prize."
                ),
                DetailDescription1: "",
                DetailDescription2: "",
                ExampleLine1Preunderline: this.getLocalizedMessage(
                    "Bracket_Tournament_Example_Line1_1",
                    "Average Handle Time"
                ),
                ExampleLine2: "00:03:00",
                ExampleLine3: this.getLocalizedMessage(
                    "Bracket_Tournament_Example_Line3",
                    "Player wins to get to the next match."
                ),
                ExampleLine4: "",
                MetricType: "Count based",
                RewardOptions: rewards.Coins | rewards.XP | rewards.Custom | rewards.Badge,
                RewardOptionsText: (RewardOptions) => {
                    return this.data.rewardOptions.textFromValue(RewardOptions);
                },
                Active: 1,
                Categories: [this.data.gameCategories.Multiplayer],
                isNewGame:true  //This should be removed in the next release to remove the “New Game” badge.
            }
        ];
    }

    ruleTemplate(): any {
        return {
            RuleID: 0,
            CompanyID: 0,
            GameType: 0,
            RuleName: "",
            RuleDescription: "",
            RuleLevel: 0,
            StartDate: null,
            EndDate: null,
            IntervalType: null,
            IntervalAmount: 0,
            SubmittedBy: 0,
            DateAdded: null,
            RuleFolder: "",
            Timestamp: "",
            IsCopy: 0,
            Active: 1,
            DateRemoved: null,
            RuleType: 2,
            FolderID: 0,
            //Specific games fields
            Category: 0,
        };
    }

    multiplierMadnessTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 1;
        return root;
    }

    topDogTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 2;
        return root;
    }

    bullseyeTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 3;
        return root;
    }

    percentileTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 4;
        return root;
    }

    milestoneTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 5;
        return root;
    }

    connect3Template(): any {
        var root = this.ruleTemplate();
        root.GameType = 6;
        return root;
    }

    blackoutTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 7;
        return root;
    }

    topTeamTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 8;
        root.AutoJoin = true;
        return root;
    }

    raffleTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 9;
        return root;
    }

    bracketTournamentTemplate(): any {
        var root = this.ruleTemplate();
        root.GameType = 10;
        return root;
    }

    getGameTypeMetricFilter(gameType: GameType): FilterMetricsParams {
        if (gameType === GameType.Milestone)
            return { DefaultDirectionFilter: 1, ConversionTypeFilter: '0', filterRealTimeMetrics: true, allMetricsFilter: true };
        if (gameType === GameType.MultiplierMadness)
            return { ConversionTypeFilter: '0', filterRealTimeMetrics: true, allMetricsFilter: true }
        return { filterRealTimeMetrics: true, allMetricsFilter: true };
    }

    // *******************************************************************************
    //                       FUNCTIONS RELATED TO GAMES WIDGET
    // *******************************************************************************
    
    async loadData(moduleKey: string, loginId: number) {
        try {
            let url: string = "/api/v2/dashboards/modules/agentgames/" + moduleKey + "/loginId/" + loginId;
            let response: any = await this.cvApiService.get(url);

            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading data: " + error.message);
        }
    }

    async getBadges(loginID: number) {
        try {
            let url: string = "/api/v1/gamification/profile/" + loginID + "/badges";
            let response: any = await this.cvApiService.get(url);
            
            if (response.status_code == 200) {
                this.data.totalBadges = response?.data?.length;
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading badges: " + error.message);
        }
    }

    async getRewards(loginID: number, gameEarningsDateTime: string) {
        try {
            let url: string = "/api/v1/gamification/profile/" + loginID + "/game-rewards?start=" + gameEarningsDateTime;
            let response: any = await this.cvApiService.get(url);

            if (response.status_code == 200) {                
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading rewards: " + error.message);
        }
    }

    async joinWidgetGame(moduleKey: number, gameId: string) {
        try {
            let url: string = "/api/v2/dashboards/modules/joingame/" + moduleKey + "/gameId/" + gameId;
            let response: any = await this.cvApiService.get(url);
            
            if (response.status_code == 200) {                
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading join games: " + error.message);
        }
    }

    async getCurrentEarnings(moduleKey: number, gameId: string, loginID: string) {
        try {
            let url: string = "/api/v2/dashboards/modules/currentdata/" + moduleKey + "/gameId/" + gameId + "/loginId/" + loginID;
            let response: any = await this.cvApiService.get(url);

            if (response.status_code == 200) {                
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading current earnings: " + error.message);
        }
    }

    async getMyTeamRewards(moduleKey: number, rewardType: string, loginID: string, gameEarningsDateTime: string) {
        try {
            let url: string = "/api/v1/gamification/games/"+ loginID + "/group-rewards?start=" + gameEarningsDateTime + "&ActionType=" + rewardType;
            let response: any = await this.cvApiService.get(url);

            if (response.status_code == 200) {                
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading teams rewards: " + error.message
            );
        }
    }

    async collectRewards(moduleKey: number, actionHistoryIds: Array<any>) {
        try {
            let url: string = "/api/v2/dashboards/modules/collect-rewards/" + moduleKey;
            let response = await this.cvApiService.put(url, actionHistoryIds);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Failed getting module render endpoint. ");
        }
    }

    async exportGamesHistory(ruleID: number, format: string, startDate, endDate, body){
        try {
            const url='/api/v1/gamification/game/' + ruleID + '/participants/export?format=' + format +'&start=' + startDate + '&end=' + endDate;
            const response = await this.cvApiService.post(url, body);
            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
                showErrorMessage("Error downloading file " + error.Message); // showErrorMessage is a global function
        }
        return '';
    }

    async getGamesData(moduleKey: string, loginID: number) {
        try {
            let url: string = "/api/v2/cxone/dashboards/modules/games/" + moduleKey + "/loginId/" + loginID;
            let response: any = await this.cvApiService.get(url);
            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage("Error loading games data: " + error.message);
        }
    }

    async getGamesRanking(moduleKey: string, loginID: number, gameId: number, qualifierId: number = null) {
        try {
            let url: string = "/api/v2/cxone/dashboards/modules/games/ranking/" + moduleKey + "/loginId/" + loginID + "/gameId/" + gameId;
            if (qualifierId) {
                url = url + '?qualifierId=' + qualifierId;
            }
            let response: any = await this.cvApiService.get(url);
            if (response.status_code == 200) {
                return response.data;
            }
        } catch (error) {
            showErrorMessage("Error getting rankings: " + error.message);
        }
    }

    private getCacheKey(RuleID: number, metricID: string, startDate: string, endDate: string): string {
        return `${RuleID}-${metricID}-${startDate}-${endDate}`;
    }

    public async LoadMetricRanking(metricID: string, emp: any, startDate: string = null, endDate: string = null, isHealthReport: boolean = false, RuleID: number = 0, abort?: Promise<void>) {
        // check if we are in a health report context, then we should reuse this request in order to avoid multiple requests for the same data
        const shouldUseCache = isHealthReport && startDate && endDate;
        const cacheKey = this.getCacheKey(RuleID, metricID, startDate, endDate);

        try {
            let url: string = "/api/v2/gamification/games/ranking/" + metricID;
            if (shouldUseCache) {
                if (this.pendingRequests.has(cacheKey)) {
                    // If a request is already pending for this cache key, wait for it to complete
                    return (await this.pendingRequests.get(cacheKey)).data;
                }
                // If the response is already cached, return it
                if (this.cache.has(cacheKey)) {
                    return this.cache.get(cacheKey);
                }
            }

            //Append startDate if it is provided
            if (startDate !== null) {
                const startDateObj = new Date(startDate);
                if (!isNaN(startDateObj.getTime())) {
                    url += `/${startDateObj.toISOString().split('T')[0]}`;
                }
            }

            // Append endDate if it is provided
            if (endDate !== null) {
                const endDateObj = new Date(endDate);
                if (!isNaN(endDateObj.getTime())) {
                    url += `/${endDateObj.toISOString().split('T')[0]}`;
                }
            }

            // Append isHealthReport if it is true
            if (isHealthReport) {
                url += `?isHealthReport=${isHealthReport}&RuleID=${RuleID}`;
            }

            const promise = this.cvApiService.post(url, emp);
            if (shouldUseCache) {
                this.pendingRequests.set(cacheKey, promise);
            }
            let response: any = await promise;
            if (response.status_code == 200) {
                if (shouldUseCache) {
                    if (!this.cache.has(cacheKey)) {
                        // If the response is successful and not cached, store it in the cache
                        this.cache.set(cacheKey, response.data);
                    }
                }
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(error.message);
        } finally {
            if (shouldUseCache && this.pendingRequests.has(cacheKey)) {
                // Remove the pending request from the map after completion
                this.pendingRequests.delete(cacheKey);
            }
        }
    }

    public async GetParentGroupName(GroupID:number, ParentGroupID: number, GroupLevel: number = null, BaseGroupType: number = null) {
        try {
            let url: string = `/api/v1/gamification/games/parent-group-name/${GroupID}/${ParentGroupID}?GroupLevel=${GroupLevel}&BaseGroupType=${BaseGroupType}`;

            let response: any = await this.cvApiService.get(url);
            if (response.status_code == 200) {
                return response.data;
            } else {
                throw response;
            }
        } catch (error) {
            showErrorMessage(error.message);
        }
    }

    formatterMetricData(conversionType: string, value: number): any {
        let formatted: any;
        if (value == undefined || value == null) value = 0;

        switch (conversionType) {
            case "1": //Second
                formatted = value;
                break;
            case "2": //Minute
                formatted = value / 60;
                break;
            case "3": //Hour
                formatted = Math.floor((value / 3600) * 100) / 100;
                break;
            case "4": //Time
                let hours = Math.floor(value / 3600);
                let minutes = Math.floor((value - (hours * 3600)) / 60);
                //Added to format this value as in PM
                let seconds = Math.floor(value - (hours * 3600) - (minutes * 60));

                let hh = (hours < 10) ? '0' + hours : hours;
                let mm = (minutes < 10) ? '0' + minutes : minutes;
                let ss = (seconds < 10) ? '0' + seconds : seconds;

                formatted = hh + ':' + mm + ':' + ss;
                break;
            case "5": //Percent
                formatted = (value * 100).toFixed(2) + '%';
                break;
            default:
                formatted = value.toFixed(2);
                break;
        }
    
        return formatted;
    }

    getMetricValue(conversionType: string, metricObject): any {
        if (metricObject && metricObject?.MetricGroupInfos[0] && metricObject?.MetricGroupInfos[0]?.MyMetricValue) {
            return this.formatterMetricData(conversionType, metricObject?.MetricGroupInfos[0]?.MyMetricValue);
        } else {
            return "--";
        }        
    }

    /**
   * Downloads a file from a byte array string
   *
   * @param data The byte[] string returned from the endpoint
   * @param mime The mime type to mark the file as
   * @param fileName The name of the file to be downloaded
   * @memberof ModuleContainerComponent
   */
    downloadFile(data: string, mime: string, fileName: string) {
        const decodedData = atob(data);
        const buffer = new ArrayBuffer(decodedData.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i !== decodedData.length; ++i) {
            // eslint-disable-next-line no-bitwise
            view[i] = decodedData.charCodeAt(i) & 0xFF;
        }
        
        const fileData = [buffer];
        const blobObject = new Blob(fileData, { type: mime });
        
        // create fake link
        const linkElement = document.createElement('a');
        document.body.appendChild(linkElement);
        linkElement.setAttribute('style', 'display: none;');
        const url = URL.createObjectURL(blobObject);
        linkElement.href =  url;
        linkElement.download = fileName;
        
        // start download
        linkElement.click();
        
        // cleanup
        URL.revokeObjectURL(linkElement.href);
        linkElement.remove();
    };

    /**
     * Checks if a given value meets the qualification criteria based on the game's qualifier levels.
     *
     * @param myValue The numeric value to be compared against the qualifier levels.
     * @returns boolean True if the value qualifies, false otherwise.
     */
    isQualified(myValue: number, game: GameObject, ranking: RankingByLogin): boolean {
        if (
            game === null ||
            ranking === null ||
            ranking?.gameRewards?.MyRankingObj === null
        )
            return false;

        if (game?.Qualifier === null) return true;

        let { comparerType, leftNumber, rightNumber } = game.QualifierLevels;

        if (game?.Qualifier?.conversionType === '5') {
            leftNumber = parseFloat(this.formatterMetricData('5', leftNumber)) / 100;
            rightNumber = parseFloat(this.formatterMetricData('5', rightNumber)) / 100;
        }

        switch (comparerType) {
            case 1: // Greater than
                return myValue > leftNumber;
            case 2: // Less than
                return myValue < leftNumber;
            case 3: // Between
                //check if the left number is greater than the right number
                if (leftNumber > rightNumber) {
                    return myValue >= rightNumber && myValue <= leftNumber;
                }
                return (
                    myValue >= leftNumber &&
                    myValue <= (rightNumber ?? leftNumber)
                );
            default:
                return false;
        }
    }

    /**
     * Checks if a connect3 game has a winner based on the win cases 
     *
     * @param metricCompleted The array of result metrics of the game
     * @returns boolean True if you are a winner of the game, false otherwise.
     */
    isWinnerConnect3(metricCompleted) {
        let bin = 0;
        for (let i = 0; i < metricCompleted.length; i++) {
            bin += (metricCompleted[i]) ? Math.pow(2, i) : 0;
        }

        for (let i = 0; i < this.data.winCasesConnect3.length; i++) {
            let result = bin & this.data.winCasesConnect3[i];
            if (result === this.data.winCasesConnect3[i]) {
                return true;
            }
        }

        return false;
    }

    /**
     * Checks if a connect all (Blackout) game has a winner based on the win cases 
     *
     * @param metricCompleted The array of result metrics of the game
     * @returns boolean True if you are a winner of the game, false otherwise.
     */
    isWinnerBlackout(metricCompleted) {
        let bin = 0;
        for (let i = 0; i < metricCompleted.length; i++) {
            bin += (metricCompleted[i]) ? Math.pow(2, i) : 0;
        }

        for (let i = 0; i < this.data.winCasesBlackout.length; i++) {
            let result = bin & this.data.winCasesBlackout[i];
            if (result === this.data.winCasesBlackout[i]) {
                return true;
            }                
        }

        return false;
    }

    pushContext(context: NavigationContext) {
        this._contextStack.push(context);
    }

    popContext(): NavigationContext | undefined {
        return this._contextStack.pop();
    }

    peekContext(): NavigationContext | undefined {
        return this._contextStack[this._contextStack.length - 1];
    }

    getContextStack(): NavigationContext[] {
        return [...this._contextStack];
    }

    clearContext() {
        this._contextStack = [];
    }

    navigateTo(target: NavigationTarget) {
        this._navigationSubject.next(target);
    }

    resetNavigation() {
        this._navigationSubject.next(null);
    }

    reorderMetricGroupInfosByGame(ranking, games) {

        const ruleId = ranking?.gameRewards?.Rule?.RuleID;
        const metricGroupInfos = ranking?.gameRewards?.MyRankingObj?.MetricGroupInfos;

        if (!Array.isArray(metricGroupInfos)) return [];

        const matchingGame = games.find(game => game?.underlyingRule?.RuleID === ruleId);
        if (!matchingGame) return [...metricGroupInfos];

        const selectedMetrics = Array.isArray(matchingGame.SelectedMetrics) && matchingGame?.SelectedMetrics?.length === 1
            ? matchingGame?.SelectedMetrics[0]
            : null;
        const qualifierMetricID = matchingGame?.Qualifier?.metricID ?? null;

        if (selectedMetrics === null && qualifierMetricID === null) {
            return [...metricGroupInfos];
        }

        const firstSelectedID = selectedMetrics?.metricID;
        if (
            firstSelectedID !== undefined &&
            qualifierMetricID !== undefined &&
            firstSelectedID === qualifierMetricID &&
            metricGroupInfos.length <= 2
        ) {
            // If both IDs are the same, do not reorder
            return [...metricGroupInfos];
        }

        const firstSelectedMetric = metricGroupInfos.find(m => m.MetricID === firstSelectedID) || null;

        const qualifierMetric = metricGroupInfos.find(m => m.MetricID === qualifierMetricID && m.RuleMetricID != firstSelectedMetric?.RuleMetricID) || null;

        const middleMetrics = metricGroupInfos.filter(m =>
            m.MetricID !== firstSelectedID && m.MetricID !== qualifierMetricID
        );

        let reordered = [];

        if (metricGroupInfos.length <= 2) {
            if (!firstSelectedMetric) {
                reordered.push({});
            } else {
                reordered.push(firstSelectedMetric);
            }
            reordered.push(...middleMetrics);
            if (!qualifierMetric) {
                reordered.push({});
            } else {
                reordered.push(qualifierMetric);
            }
        } else {
            if (firstSelectedMetric) reordered.push(firstSelectedMetric);
            reordered.push(...middleMetrics);
            if (qualifierMetric && qualifierMetric.MetricID !== firstSelectedID) {
                reordered.push(qualifierMetric);
            }
        }

        if (ranking?.gameRewards?.MyRankingObj) {
            ranking.gameRewards.MyRankingObj.MetricGroupInfos = reordered;
        }
        return reordered;
    }

    saveSelectedInterval(interval: any, gameRuleID?: number): void {
        this.lastSelectedInterval = {
            StartDate: interval.StartDate,
            EndDate: interval.EndDate,
            DisplayStringDate: interval.DisplayStringDate,
            isSelected: true
        };

        if (gameRuleID) {
            this.lastSelectedGameRuleID = gameRuleID;
        }
    }

    saveSelectedHistoryView(historyView: any, gameRuleID?: number): void {
        this.lastSelectedHistoryView = historyView;
        
        if (gameRuleID) {
            this.lastSelectedGameRuleID = gameRuleID;
        }
    }

    restoreSelectedInterval(gameIntervals: any[], currentGameRuleID?: number): any | null {
        if (this.lastSelectedGameRuleID && currentGameRuleID && 
            this.lastSelectedGameRuleID !== currentGameRuleID) {
            this.clearSavedInterval();
            return null;
        }

        if (!this.lastSelectedInterval || !gameIntervals || gameIntervals.length === 0) {
            return null;
        }
        
        return gameIntervals.find(interval => 
            interval.EndDate === this.lastSelectedInterval.EndDate && 
            interval.StartDate === this.lastSelectedInterval.StartDate
        );
    }

    restoreSelectedHistoryView(currentGameRuleID?: number): any | null {
        if (this.lastSelectedGameRuleID && currentGameRuleID && 
            this.lastSelectedGameRuleID !== currentGameRuleID) {
            this.clearSavedHistoryView();
            return null;
        }

        return this.lastSelectedHistoryView;
    }

    clearSavedInterval(): void {
        this.lastSelectedInterval = null;
    }

    clearSavedHistoryView(): void {
        this.lastSelectedHistoryView = null;
    }

    clearSavedData(): void {
        this.clearSavedInterval();
        this.clearSavedHistoryView();
        this.lastSelectedGameRuleID = null;
    }
}
